/**
 * Compliance Pages — Discipline (§29), Health & Safety (§30), Audit Log (§42), Reminders (§43).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, X, ShieldAlert, AlertTriangle, ScrollText, Bell, User, Clock } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const Card = ({ children, className = '' }) => (<div className={`bg-white border border-black/5 rounded-2xl ${className}`}>{children}</div>);
const H1 = ({ eyebrow, title, sub }) => (
  <div className="mb-6"><span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">{eyebrow}</span>
    <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{title}</h1>
    {sub && <p className="mt-1 text-sm text-[#0b1a2e]/60">{sub}</p>}</div>
);
const Btn = ({ variant='primary', className='', ...r }) => {
  const s = { primary:'bg-[#0f5c3d] text-white hover:bg-[#0b4a30]', ghost:'border border-[#0f5c3d]/20 text-[#0f5c3d] hover:bg-[#f7fbf8]' }[variant];
  return <button {...r} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${s} ${className}`}/>;
};
const Input = (p) => <input {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm ${p.className || ''}`}/>;
const Select = (p) => <select {...p} className={`w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white ${p.className || ''}`}>{p.children}</select>;
const Field = ({ label, children }) => (<div><label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>{children}</div>);
const Badge = ({ tone='green', children }) => <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${{green:'bg-emerald-100 text-emerald-800',amber:'bg-amber-100 text-amber-800',red:'bg-red-100 text-red-800',slate:'bg-slate-100 text-slate-700',sky:'bg-sky-100 text-sky-800'}[tone]}`}>{children}</span>;

// ============================================================
// DISCIPLINE / GRIEVANCE
// ============================================================
export const DisciplinePage = () => {
  const [items, setItems] = useState([]); const [users, setUsers] = useState([]); const [showNew, setShowNew] = useState(false); const [selected, setSelected] = useState(null);
  const load = useCallback(async () => { try { const [a,b] = await Promise.all([apiClient.get('/hr/cases'), apiClient.get('/users/directory')]); setItems(a.data.data||[]); setUsers(b.data.data||[]); if (selected){const f=(a.data.data||[]).find((x)=>x.id===selected.id); if(f)setSelected(f);} } catch(e){toast({title:'Access denied',variant:'destructive'});} }, [selected]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §29 · Confidential" title="Discipline & grievance" sub="Restricted to HR/admin. Case number, investigation, meetings, decision, appeal, closure." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setShowNew(true)} data-testid="new-case-btn"><Plus className="w-3.5 h-3.5"/> New case</Btn></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-5">
          <div className="p-3 border-b border-black/5 text-xs text-[#0b1a2e]/60">{items.length} cases</div>
          <div className="max-h-[65vh] overflow-y-auto">
            {items.map((c)=>(
              <button key={c.id} onClick={()=>setSelected(c)} data-testid={`case-row-${c.case_no}`}
                className={`w-full text-left p-3 border-b border-black/5 hover:bg-[#f7fbf8] ${selected?.id===c.id?'bg-[#f7fbf8]':''}`}>
                <div className="flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5 text-red-600"/><span className="text-sm font-mono font-semibold">{c.case_no}</span><Badge tone={c.kind==='disciplinary'?'red':'sky'}>{c.kind}</Badge><Badge tone={c.severity==='critical'?'red':c.severity==='high'?'amber':'slate'}>{c.severity}</Badge></div>
                <div className="text-xs text-[#0b1a2e]/70 mt-1">{c.subject_name} · <span className="text-[#0b1a2e]/50">{c.stage}</span></div>
              </button>
            ))}
            {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No cases.</div>}
          </div>
        </Card>
        <div className="lg:col-span-7">
          {!selected ? <Card className="p-16 text-center text-sm text-[#0b1a2e]/50">Select a case to view details.</Card> : <CaseDetail case_={selected} onChanged={load} />}
        </div>
      </div>
      {showNew && <CaseModal users={users} onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false);load();}} />}
    </div>
  );
};

const CaseDetail = ({ case_, onChanged }) => {
  const [ev, setEv] = useState({ kind:'note', note:'', outcome:'' });
  const add = async () => {
    if (!ev.note) return;
    try { await apiClient.post(`/hr/cases/${case_.id}/events`, ev); toast({title:'Added'}); setEv({kind:'note',note:'',outcome:''}); onChanged(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="space-y-3">
      <Card className="p-4">
        <div className="font-mono text-xs text-[#0b1a2e]/60">{case_.case_no}</div>
        <div className="text-sm font-semibold mt-1">{case_.subject_name}</div>
        <div className="text-xs text-[#0b1a2e]/70 mt-1">{case_.issue}</div>
        <div className="mt-2 flex gap-2 items-center"><Badge tone={case_.status==='closed'?'slate':'amber'}>{case_.status}</Badge><span className="text-xs">Stage: {case_.stage}</span></div>
        {case_.decision && <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded p-2">Decision: {case_.decision}</div>}
        {case_.appeal && <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2">Appeal: {case_.appeal}</div>}
      </Card>
      <Card>
        <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60">Events</div>
        <div className="max-h-[35vh] overflow-y-auto">
          {(case_.events||[]).map((e,i)=>(<div key={i} className="p-3 border-b border-black/5"><div className="flex justify-between text-xs"><Badge tone={e.kind==='decision'?'green':e.kind==='closure'?'slate':'sky'}>{e.kind}</Badge><span className="text-[#0b1a2e]/50">{(e.at||'').slice(0,16).replace('T',' ')}</span></div><div className="text-sm mt-1">{e.note}</div>{e.outcome && <div className="text-xs text-[#0b1a2e]/60 mt-1">Outcome: {e.outcome}</div>}</div>))}
        </div>
        <div className="p-3 border-t bg-[#f7fbf8] grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={ev.kind} onChange={(e)=>setEv({...ev,kind:e.target.value})} data-testid="case-event-kind">{['note','meeting','document','decision','appeal','closure'].map((k)=><option key={k} value={k}>{k}</option>)}</Select>
          <Input value={ev.note} onChange={(e)=>setEv({...ev,note:e.target.value})} placeholder="Note..." className="sm:col-span-2" data-testid="case-event-note"/>
          <Btn onClick={add} data-testid="case-event-add">Add</Btn>
        </div>
      </Card>
    </div>
  );
};

const CaseModal = ({ users, onClose, onSaved }) => {
  const [f, setF] = useState({ kind:'disciplinary', subject_user_id:'', issue:'', severity:'medium', notes:'' });
  const save = async () => {
    try { await apiClient.post('/hr/cases', f); toast({title:'Case opened'}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">New case</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Kind"><Select value={f.kind} onChange={(e)=>setF({...f,kind:e.target.value})}><option value="disciplinary">Disciplinary</option><option value="grievance">Grievance</option></Select></Field>
          <Field label="Severity"><Select value={f.severity} onChange={(e)=>setF({...f,severity:e.target.value})}>{['low','medium','high','critical'].map((s)=><option key={s} value={s}>{s}</option>)}</Select></Field>
          <div className="col-span-2"><Field label="Subject employee"><Select value={f.subject_user_id} onChange={(e)=>setF({...f,subject_user_id:e.target.value})} data-testid="case-subject"><option value="">Select</option>{users.map((u)=><option key={u.user_id} value={u.user_id}>{u.name||u.email}</option>)}</Select></Field></div>
          <div className="col-span-2"><Field label="Issue"><Input value={f.issue} onChange={(e)=>setF({...f,issue:e.target.value})} data-testid="case-issue"/></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="case-save">Open case</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// HEALTH & SAFETY
// ============================================================
export const SafetyPage = () => {
  const [items, setItems] = useState([]); const [showNew, setShowNew] = useState(false);
  const load = useCallback(async () => { const r = await apiClient.get('/hr/safety/incidents'); setItems(r.data.data||[]); }, []);
  useEffect(() => { load(); }, [load]);
  const close_ = async (i) => { const n = window.prompt('Closure note:',''); if (!n) return; await apiClient.post(`/hr/safety/incidents/${i.id}/close`, {closure_note:n}); load(); };
  return (
    <div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <H1 eyebrow="Global HR · §30" title="Health & safety" sub="Accidents, incidents, near-misses, injuries. Investigation and corrective action tracking." />
        <div className="flex gap-2 mb-6"><Btn onClick={()=>setShowNew(true)} data-testid="new-incident-btn"><Plus className="w-3.5 h-3.5"/> Report incident</Btn></div>
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">Date</th><th className="text-left p-2">Kind</th><th className="text-left p-2">Location</th><th className="text-left p-2">Severity</th><th className="text-left p-2">Description</th><th className="text-left p-2">Status</th><th></th></tr></thead>
          <tbody>{items.map((i)=>(
            <tr key={i.id} className="border-t border-black/5" data-testid={`incident-row-${i.id}`}>
              <td className="p-2 text-xs">{i.date}</td>
              <td className="p-2"><AlertTriangle className="w-3.5 h-3.5 inline text-amber-600 mr-1"/><span className="text-xs">{i.kind}</span></td>
              <td className="p-2 text-xs">{i.location}</td>
              <td className="p-2"><Badge tone={i.severity==='critical'?'red':i.severity==='major'?'amber':'slate'}>{i.severity}</Badge></td>
              <td className="p-2 text-xs max-w-[240px] truncate">{i.description}</td>
              <td className="p-2"><Badge tone={i.status==='closed'?'slate':'amber'}>{i.status}</Badge></td>
              <td className="p-2 text-right">{i.status==='open' && <Btn variant="ghost" onClick={()=>close_(i)} data-testid={`incident-close-${i.id}`}>Close</Btn>}</td>
            </tr>
          ))}</tbody>
        </table>
        {items.length===0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No incidents.</div>}
      </Card>
      {showNew && <IncidentModal onClose={()=>setShowNew(false)} onSaved={()=>{setShowNew(false);load();}} />}
    </div>
  );
};

const IncidentModal = ({ onClose, onSaved }) => {
  const [f, setF] = useState({ kind:'incident', date:new Date().toISOString().slice(0,10), location:'', severity:'minor', involved_user_ids:[], description:'', country:'', investigation_notes:'', corrective_action:'' });
  const save = async () => {
    try { await apiClient.post('/hr/safety/incidents', f); toast({title:'Reported'}); onSaved(); }
    catch(e){ toast({title:e?.response?.data?.detail||'Failed',variant:'destructive'}); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">Report incident</div><button onClick={onClose}><X className="w-5 h-5"/></button></div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Kind"><Select value={f.kind} onChange={(e)=>setF({...f,kind:e.target.value})}>{['accident','incident','near_miss','injury'].map((k)=><option key={k} value={k}>{k}</option>)}</Select></Field>
          <Field label="Severity"><Select value={f.severity} onChange={(e)=>setF({...f,severity:e.target.value})}>{['minor','moderate','major','critical'].map((s)=><option key={s} value={s}>{s}</option>)}</Select></Field>
          <Field label="Date"><Input type="date" value={f.date} onChange={(e)=>setF({...f,date:e.target.value})} data-testid="inc-date"/></Field>
          <Field label="Country"><Input value={f.country} onChange={(e)=>setF({...f,country:e.target.value.toUpperCase()})}/></Field>
          <div className="col-span-2"><Field label="Location"><Input value={f.location} onChange={(e)=>setF({...f,location:e.target.value})} data-testid="inc-location"/></Field></div>
          <div className="col-span-2"><Field label="Description"><Input value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} data-testid="inc-desc"/></Field></div>
          <div className="col-span-2"><Field label="Corrective action"><Input value={f.corrective_action} onChange={(e)=>setF({...f,corrective_action:e.target.value})}/></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} data-testid="inc-save">Report</Btn></div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// AUDIT LOG
// ============================================================
export const AuditLogPage = () => {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  useEffect(() => { apiClient.get('/hr/audit/log').then((r)=>setItems(r.data.data||[])).finally(()=>setLoading(false)); }, []);
  const filtered = items.filter((i) => !filter || i.action?.includes(filter) || i.entity_type?.includes(filter) || i.entity_id?.includes(filter));
  return (
    <div>
      <H1 eyebrow="Global HR · §42" title="Audit trail" sub="Every high-impact change (salary, permissions, terminations, exports) is logged." />
      <div className="mb-3"><Input placeholder="Filter by action / entity..." value={filter} onChange={(e)=>setFilter(e.target.value)} data-testid="audit-filter"/></div>
      <Card>
        {loading ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 inline animate-spin"/></div> : (
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60"><tr><th className="text-left p-2">When</th><th className="text-left p-2">Actor</th><th className="text-left p-2">Action</th><th className="text-left p-2">Entity</th><th className="text-left p-2">Approver</th></tr></thead>
            <tbody>{filtered.map((i)=>(
              <tr key={i.id} className="border-t border-black/5" data-testid={`audit-row-${i.id}`}>
                <td className="p-2 text-xs">{(i.created_at||'').slice(0,16).replace('T',' ')}</td>
                <td className="p-2 text-xs font-mono">{i.user_id}</td>
                <td className="p-2"><ScrollText className="w-3.5 h-3.5 inline text-[#0f5c3d] mr-1"/><span className="text-xs">{i.action}</span></td>
                <td className="p-2 text-xs">{i.entity_type} · <span className="font-mono text-[#0b1a2e]/50">{i.entity_id}</span></td>
                <td className="p-2 text-xs">{i.approver||'—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {filtered.length===0 && !loading && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No audit events.</div>}
      </Card>
    </div>
  );
};

// ============================================================
// REMINDERS
// ============================================================
export const RemindersPage = () => {
  const [d, setD] = useState(null);
  useEffect(() => { apiClient.get('/hr/reminders').then((r)=>setD(r.data)).catch(()=>setD({counts:{}})); }, []);
  if (!d) return <div className="p-10 text-center"><Loader2 className="w-5 h-5 inline animate-spin"/></div>;
  const c = d.counts || {};
  const bucketColor = (b) => b==='7d'?'red':b==='14d'?'red':b==='30d'?'amber':'slate';
  return (
    <div>
      <H1 eyebrow="Global HR · §22 · §37 · §43" title="Reminders radar" sub="Probation ending · Contracts expiring · Visas expiring · Certificates expiring." />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[['Probation',c.probation||0,'amber'],['Contracts',c.contracts||0,'red'],['Visas',c.visas||0,'red'],['Certificates',c.certificates||0,'sky']].map(([l,v,t])=>(
          <Card key={l} className={`p-3 border ${{amber:'bg-amber-50 border-amber-200',red:'bg-red-50 border-red-200',sky:'bg-sky-50 border-sky-200'}[t]}`}>
            <Bell className="w-4 h-4 mb-1"/>
            <div className="text-2xl font-bold">{v}</div>
            <div className="text-[10px] uppercase tracking-widest font-semibold">{l}</div>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RemSection title="Probation ending" rows={(d.probation_ending||[]).map((u)=>({key:u.user_id,left:u.name,right:u.probation_end_date,days:u.days_left,bucket:u.bucket}))} bucketColor={bucketColor}/>
        <RemSection title="Contracts expiring" rows={(d.contracts_expiring||[]).map((u)=>({key:u.user_id,left:u.name,right:u.contract_end_date,days:u.days_left,bucket:u.bucket}))} bucketColor={bucketColor}/>
        <RemSection title="Visas / IDs expiring" rows={(d.visas_expiring||[]).map((v)=>({key:v.id,left:v.title,right:v.expires_on,days:v.days_left,bucket:v.bucket}))} bucketColor={bucketColor}/>
        <RemSection title="Training certificates" rows={(d.certificates_expiring||[]).map((c)=>({key:c.id,left:c.course_title,right:c.certificate_expires_on,days:c.days_left,bucket:c.bucket}))} bucketColor={bucketColor}/>
      </div>
    </div>
  );
};

const RemSection = ({ title, rows, bucketColor }) => (
  <Card>
    <div className="p-3 border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/60 flex items-center gap-2">
      <Clock className="w-3.5 h-3.5 text-[#0f5c3d]"/> {title} <span className="text-[10px] text-[#0b1a2e]/40">({rows.length})</span>
    </div>
    <div className="max-h-[320px] overflow-y-auto">
      {rows.length===0 ? <div className="p-4 text-xs text-[#0b1a2e]/40">Nothing coming up.</div> : rows.map((r)=>(
        <div key={r.key} className="p-2 border-b border-black/5 flex items-center gap-2 text-sm" data-testid={`rem-row-${r.key}`}>
          <User className="w-3.5 h-3.5 text-[#0f5c3d]"/>
          <div className="flex-1 min-w-0 truncate">{r.left}</div>
          <div className="text-xs text-[#0b1a2e]/60">{r.right}</div>
          <Badge tone={bucketColor(r.bucket)}>{r.days}d</Badge>
        </div>
      ))}
    </div>
  </Card>
);
